import { createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './auth/AuthProvider';
import { Button, Card, CenteredScreen, ErrorText } from './components/ui';
import { AppLayout } from './app/AppLayout';
import { ViewContextProvider } from './app/ViewContext';
import { CarePage } from './care/CarePage';
import { ClubsPage } from './clubs/ClubsPage';
import { type LabTab, LabPage } from './lab/LabPage';
import { ModelPage, PublicModelPage } from './lab/ModelPage';
import { SimPage } from './lab/SimPage';
import { LettersPage } from './student/LettersPage';
import { SettingsPage } from './student/SettingsPage';
import { NmtPage } from './student/NmtPage';
import { SeasonPage } from './student/SeasonPage';
import { YearBookPage } from './student/YearBookPage';
import { FuturePage } from './student/FuturePage';
import { AbroadPage } from './student/AbroadPage';
import { FormsPage } from './pages/FormsPage';
import { MovingPage } from './pages/MovingPage';
import { LessonPackPage } from './pages/LessonPackPage';
import { RewardsPage } from './student/RewardsPage';
import { TreePage } from './student/TreePage';
import { AchievementsPage } from './pages/AchievementsPage';
import { AdminPage } from './pages/AdminPage';
import { AlbumPage } from './pages/AlbumPage';
import { AlarmPage } from './pages/AlarmPage';
import { AnnouncementsPage } from './pages/AnnouncementsPage';
import { CanteenPage } from './pages/CanteenPage';
import { EventsPage } from './pages/EventsPage';
import { GradesPage } from './pages/GradesPage';
import { HomeworkPage } from './pages/HomeworkPage';
import { HomePage } from './pages/HomePage';
import { LifePage } from './pages/LifePage';
import { CatchUpPage } from './pages/CatchUpPage';
import { JournalPage } from './pages/JournalPage';
import { SchedulePage } from './pages/SchedulePage';
import { InvitePage } from './pages/InvitePage';
import { LoginPage } from './pages/LoginPage';
import { MessagesPage } from './pages/MessagesPage';
import { MfaPage } from './pages/MfaPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { MaterialsPage } from './pages/MaterialsPage';
import { PlanningPage } from './pages/PlanningPage';
import { MeetingsPage } from './pages/MeetingsPage';
import { PollsPage } from './pages/PollsPage';
import { QuizzesPage } from './pages/QuizzesPage';
import { RequestsPage } from './pages/RequestsPage';

/** Nothing below renders until the user is signed in and (for staff) TOTP-verified. */
function AuthGate() {
  const { t } = useTranslation();
  const { step, error, refresh } = useAuth();
  if (error) {
    return (
      <CenteredScreen>
        <Card>
          <ErrorText>{t('common.error')}</ErrorText>
          <Button className="mt-4" onClick={() => void refresh()}>
            {t('common.retry')}
          </Button>
        </Card>
      </CenteredScreen>
    );
  }
  switch (step) {
    case 'loading':
      return <CenteredScreen>{t('common.loading')}</CenteredScreen>;
    case 'login':
      return <LoginPage />;
    case 'mfa':
      return <MfaPage />;
    case 'ready':
      return (
        <ViewContextProvider>
          <AppLayout />
        </ViewContextProvider>
      );
  }
}

const rootRoute = createRootRoute();
const appRoute = createRoute({ getParentRoute: () => rootRoute, id: 'app', component: AuthGate });
const homeRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: HomePage });
const scheduleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/schedule',
  component: SchedulePage,
});
const journalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/journal',
  component: JournalPage,
});
const gradesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/grades',
  component: GradesPage,
});
const homeworkRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/homework',
  component: HomeworkPage,
});
const announcementsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/announcements',
  component: AnnouncementsPage,
});
const notificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/notifications',
  component: NotificationsPage,
});
const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin',
  component: AdminPage,
});
const messagesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/messages',
  component: MessagesPage,
});
const requestsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/requests',
  component: RequestsPage,
});
const canteenRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/canteen',
  component: CanteenPage,
});
const eventsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/events',
  component: EventsPage,
});
const quizzesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/quizzes',
  component: QuizzesPage,
});
const albumRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/album',
  component: AlbumPage,
});
const materialsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/materials',
  component: MaterialsPage,
});
const achievementsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/achievements',
  component: AchievementsPage,
});
const pollsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/polls',
  component: PollsPage,
});
const careRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/care',
  // a thread opened from a notification; «calm» / «sos» opened from the alarm screen
  validateSearch: (search: Record<string, unknown>): { thread?: string; open?: string } => ({
    ...(typeof search.thread === 'string' && { thread: search.thread }),
    ...(typeof search.open === 'string' && { open: search.open }),
  }),
  component: CarePage,
});
const lettersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/letters',
  component: LettersPage,
});
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: SettingsPage,
});
const lifeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/life',
  component: LifePage,
});
const catchUpRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/catch-up',
  component: CatchUpPage,
});
const alarmRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/alarm',
  component: AlarmPage,
});
// the video client is large: it is downloaded only when a lesson is opened
const LessonRoomPage = lazy(() =>
  import('./pages/LessonRoomPage').then((m) => ({ default: m.LessonRoomPage })),
);

const lessonRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/lesson/$lessonId',
  component: function LessonRoute() {
    const { lessonId } = lessonRoute.useParams();
    return (
      <Suspense fallback={null}>
        <LessonRoomPage target={{ lessonId }} />
      </Suspense>
    );
  },
});

// the board (Excalidraw) is large too: downloaded only when a board is opened
const BoardPage = lazy(() => import('./board/BoardPage').then((m) => ({ default: m.BoardPage })));

const boardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/board/$boardId',
  component: function BoardRoute() {
    const { boardId } = boardRoute.useParams();
    return (
      <Suspense fallback={null}>
        <BoardPage boardId={boardId} />
      </Suspense>
    );
  },
});

const meetingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/meeting/$meetingId',
  component: function MeetingRoute() {
    const { meetingId } = meetingRoute.useParams();
    return (
      <Suspense fallback={null}>
        <LessonRoomPage target={{ meetingId }} />
      </Suspense>
    );
  },
});
const seasonRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/season',
  component: SeasonPage,
});
const yearbookRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/yearbook',
  component: YearBookPage,
});
const futureRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/future',
  component: FuturePage,
});
const lessonPackRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/lesson-pack/$lessonId',
  component: function LessonPackRoute() {
    const { lessonId } = lessonPackRoute.useParams();
    return <LessonPackPage lessonId={lessonId} />;
  },
});
const movingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/moving',
  component: MovingPage,
});
const rewardsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/rewards',
  component: RewardsPage,
});
const formsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/forms',
  component: FormsPage,
});
const abroadRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/abroad',
  component: AbroadPage,
});
const planningRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/planning',
  component: PlanningPage,
});
const nmtRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/nmt',
  component: NmtPage,
});
const treeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tree',
  component: TreePage,
});
const clubsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/clubs',
  component: ClubsPage,
});
const labRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/lab',
  validateSearch: (search: Record<string, unknown>): { tab?: LabTab } =>
    search.tab === 'models' || search.tab === 'sims' ? { tab: search.tab } : {},
  component: function LabRoute() {
    const { tab } = labRoute.useSearch();
    return <LabPage tab={tab ?? 'sims'} />;
  },
});
const simRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/lab/sim/$slug',
  component: function SimRoute() {
    const { slug } = simRoute.useParams();
    return <SimPage slug={slug} />;
  },
});
const modelRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/lab/model/$modelId',
  component: function ModelRoute() {
    const { modelId } = modelRoute.useParams();
    return <ModelPage id={modelId} />;
  },
});
// a 3D model without signing in: the phone app shows it, Safari puts it on the table (AR)
const publicModelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/model/$modelId',
  component: function PublicModelRoute() {
    const { modelId } = publicModelRoute.useParams();
    return <PublicModelPage id={modelId} />;
  },
});
const meetingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/meetings',
  component: MeetingsPage,
});

const ClassBoardPage = lazy(() =>
  import('./board/ClassBoardPage').then((m) => ({ default: m.ClassBoardPage })),
);
const EmbeddedClassBoardPage = lazy(() =>
  import('./board/ClassBoardPage').then((m) => ({ default: m.EmbeddedClassBoardPage })),
);
// «Дошка в класі»: the classroom computer at the projector (or `?remote=1` — the teacher's remote)
const classBoardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/class-board/$lessonId',
  validateSearch: (search: Record<string, unknown>): { remote?: boolean } =>
    search.remote === true || search.remote === '1' || search.remote === 1 ? { remote: true } : {},
  component: function ClassBoardRoute() {
    const { lessonId } = classBoardRoute.useParams();
    const { remote } = classBoardRoute.useSearch();
    return (
      <Suspense fallback={null}>
        <ClassBoardPage lessonId={lessonId} remote={remote === true} />
      </Suspense>
    );
  },
});
// the class board inside the phone app: the app brings the pass
const embeddedClassBoardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/class-board-app',
  component: function EmbeddedClassBoardRoute() {
    return (
      <Suspense fallback={null}>
        <EmbeddedClassBoardPage />
      </Suspense>
    );
  },
});

const EmbeddedRoomPage = lazy(() =>
  import('./pages/LessonRoomPage').then((m) => ({ default: m.EmbeddedRoomPage })),
);
// The lesson room inside the phone app: no sign-in here, the app brings the pass
const embeddedRoomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/room',
  component: function EmbeddedRoomRoute() {
    return (
      <Suspense fallback={null}>
        <EmbeddedRoomPage />
      </Suspense>
    );
  },
});

// Open without an account. Printed QR codes point here: /invite?code=ABCDE-FGHJK
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite',
  // the router parses search values as JSON, so an all-digit code arrives as a number
  validateSearch: (search: Record<string, unknown>): { code?: string } =>
    typeof search.code === 'string' || typeof search.code === 'number'
      ? { code: String(search.code) }
      : {},
  component: function InviteRoute() {
    const { code } = inviteRoute.useSearch();
    return <InvitePage {...(code !== undefined && { initialCode: code })} />;
  },
});

export const router = createRouter({
  routeTree: rootRoute.addChildren([
    appRoute.addChildren([
      homeRoute,
      scheduleRoute,
      journalRoute,
      gradesRoute,
      homeworkRoute,
      announcementsRoute,
      notificationsRoute,
      alarmRoute,
      careRoute,
      lettersRoute,
      settingsRoute,
      lifeRoute,
      catchUpRoute,
      canteenRoute,
      eventsRoute,
      pollsRoute,
      achievementsRoute,
      materialsRoute,
      albumRoute,
      quizzesRoute,
      lessonRoute,
      boardRoute,
      meetingRoute,
      meetingsRoute,
      treeRoute,
      nmtRoute,
      planningRoute,
      seasonRoute,
      yearbookRoute,
      futureRoute,
      abroadRoute,
      formsRoute,
      rewardsRoute,
      movingRoute,
      lessonPackRoute,
      classBoardRoute,
      clubsRoute,
      labRoute,
      simRoute,
      modelRoute,
      messagesRoute,
      requestsRoute,
      adminRoute,
    ]),
    inviteRoute,
    embeddedRoomRoute,
    embeddedClassBoardRoute,
    publicModelRoute,
  ]),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
